/**
 * Site Kit by Google, Copyright 2024 Google LLC
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     https://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Internal dependencies
 */
import { classifyPII, getUserData } from './utils';

( ( jQuery, PUM ) => {
	if ( ! jQuery || ! PUM ) {
		return;
	}

	PUM.hooks.addAction(
		'pum.integration.form.success',
		(
			form: unknown,
			// eslint-disable-next-line sitekit/acronym-case
			args: { formProvider?: string; popupId?: string | number }
		) => {
			const gtagUserDataEnabled = global._googlesitekit?.gtagUserData;

			const userData =
				gtagUserDataEnabled && shouldHandleProvider( args.formProvider )
					? getUserDataFromPMForm( form )
					: undefined;

			// Disabled because the data/property names in this form are
			// not controlled by Site Kit, thus don't conform to our ESLint
			// rules on casing.
			// eslint-disable-next-line sitekit/acronym-case
			const popupID = args?.popupId;

			global._googlesitekit?.gtagEvent?.( 'submit_lead_form', {
				googlesitekit_event_provider: 'popup-maker',
				...( popupID
					? { googlesitekit_form_id: String( popupID ) }
					: {} ),
				...( userData ? { user_data: userData } : {} ),
			} );
		}
	);
} )( global.jQuery, global.PUM );

const HANDLED_PROVIDERS = [ 'wpforms', 'contactform7', 'ninjaforms', 'mc4wp' ];

/**
 * Determines if the form provider is already handled in another script.
 *
 * @since 1.162.0
 *
 * @param {string} provider The form provider (plugin) slug.
 * @return {boolean} Whether this provider's submission should be handled here or not.
 */
function shouldHandleProvider( provider: string | undefined ): boolean {
	return provider === undefined || ! HANDLED_PROVIDERS.includes( provider );
}

/**
 * Extracts and classifies user data from a form submission inside a Popup Maker popup.
 *
 * @since 1.162.0 Renamed to `getUserDataFromPMForm` because `getUserData` was extracted into a generic utility function.
 *
 * @param {Object} form A jQuery object or an HTMLFormElement instance.
 * @return {Object|undefined} A user_data object containing detected PII (address, email, phone_number), or undefined if no PII found.
 */
function getUserDataFromPMForm( form: unknown ) {
	// eslint-disable-next-line sitekit/acronym-case
	const resolvedForm: HTMLFormElement | null =
		form instanceof HTMLFormElement
			? form
			: ( form as { 0?: HTMLFormElement | null } )?.[ 0 ] ?? null;

	if ( ! resolvedForm ) {
		return undefined;
	}

	const formData = new FormData( resolvedForm );
	const detectedFields = Array.from( formData.entries() )
		.map( ( [ name, value ] ) => {
			const input = resolvedForm.querySelector< HTMLInputElement >(
				`[name='${ name }']`
			);

			const type = input?.type;

			// Filter out hidden fields.
			if ( type === 'hidden' ) {
				return null;
			}

			// Skip file inputs — only string values are classifiable as PII.
			if ( typeof value !== 'string' ) {
				return null;
			}

			const label =
				( input?.id
					? resolvedForm.querySelector(
							`label[for='${ input?.id }']`
					  )?.textContent
					: null ) || input?.closest( 'label' )?.textContent;

			return classifyPII( {
				type,
				label,
				name,
				value,
			} );
		} )
		.filter( ( f ): f is NonNullable< typeof f > => f !== null );

	return getUserData( detectedFields );
}
